import {
  clampCropRectToBounds,
  CropAnchorAlignment,
  getCropRectAnchorPoint,
  normalizeCropRect,
  scaleCropRect,
} from './crop-rect';
import type { CropOptions, CropRect, CropSize, CropTransformOptions } from './types';
import { CropOperation, CropUnit } from './types';

const INITIAL_ASPECT_RATIO_CROP_SCALE = 0.92;

interface TransformStrategy {
  resizeN(cropRect: CropRect, options: CropTransformOptions): void;
  resizeS(cropRect: CropRect, options: CropTransformOptions): void;
  resizeW(cropRect: CropRect, options: CropTransformOptions): void;
  resizeE(cropRect: CropRect, options: CropTransformOptions): void;
  resizeNW(cropRect: CropRect, options: CropTransformOptions): void;
  resizeNE(cropRect: CropRect, options: CropTransformOptions): void;
  resizeSW(cropRect: CropRect, options: CropTransformOptions): void;
  resizeSE(cropRect: CropRect, options: CropTransformOptions): void;
}

class BaseFreeAspectTransformStrategy implements TransformStrategy {
  resizeN(_cropRect: CropRect, _options: CropTransformOptions): void {
    throw new Error('Method not implemented.');
  }

  resizeS(_cropRect: CropRect, _options: CropTransformOptions): void {
    throw new Error('Method not implemented.');
  }

  resizeW(_cropRect: CropRect, _options: CropTransformOptions): void {
    throw new Error('Method not implemented.');
  }

  resizeE(_cropRect: CropRect, _options: CropTransformOptions): void {
    throw new Error('Method not implemented.');
  }

  resizeNE(cropRect: CropRect, options: CropTransformOptions): void {
    this.resizeN(cropRect, options);
    this.resizeE(cropRect, options);
  }

  resizeNW(cropRect: CropRect, options: CropTransformOptions): void {
    this.resizeN(cropRect, options);
    this.resizeW(cropRect, options);
  }

  resizeSE(cropRect: CropRect, options: CropTransformOptions): void {
    this.resizeS(cropRect, options);
    this.resizeE(cropRect, options);
  }

  resizeSW(cropRect: CropRect, options: CropTransformOptions): void {
    this.resizeS(cropRect, options);
    this.resizeW(cropRect, options);
  }
}

class EdgeFreeAspectTransformStrategy extends BaseFreeAspectTransformStrategy {
  override resizeN(cropRect: CropRect, options: CropTransformOptions): void {
    cropRect.y += options.dy;
    cropRect.height -= options.dy;

    if (cropRect.y < 0) {
      cropRect.height += cropRect.y;
      cropRect.y = 0;
    }

    if (cropRect.y >= options.imageSize.height) {
      cropRect.height += cropRect.y - options.imageSize.height + 1;
      cropRect.y = options.imageSize.height - 1;
    }
  }

  override resizeS(cropRect: CropRect, options: CropTransformOptions): void {
    cropRect.height += options.dy;

    if (cropRect.height + cropRect.y > options.imageSize.height) {
      cropRect.height = options.imageSize.height - cropRect.y;
    }

    if (cropRect.height < -cropRect.y) {
      cropRect.height = -cropRect.y;
    }
  }

  override resizeW(cropRect: CropRect, options: CropTransformOptions): void {
    cropRect.x += options.dx;
    cropRect.width -= options.dx;

    if (cropRect.x < 0) {
      cropRect.width += cropRect.x;
      cropRect.x = 0;
    }

    if (cropRect.x >= options.imageSize.width) {
      cropRect.width += cropRect.x - options.imageSize.width + 1;
      cropRect.x = options.imageSize.width - 1;
    }
  }

  override resizeE(cropRect: CropRect, options: CropTransformOptions): void {
    cropRect.width += options.dx;

    if (cropRect.width + cropRect.x > options.imageSize.width) {
      cropRect.width = options.imageSize.width - cropRect.x;
    }

    if (cropRect.width < -cropRect.x) {
      cropRect.width = -cropRect.x;
    }
  }
}

class CenterFreeAspectTransformStrategy extends BaseFreeAspectTransformStrategy {
  override resizeN(cropRect: CropRect, options: CropTransformOptions): void {
    cropRect.y += options.dy;
    cropRect.height -= options.dy * 2;

    if (cropRect.y < 0) {
      cropRect.height += cropRect.y * 2;
      cropRect.y = 0;
    }

    if (cropRect.y >= options.imageSize.height) {
      cropRect.height += 2 * (cropRect.y - options.imageSize.height + 1);
      cropRect.y = options.imageSize.height - 1;
    }

    if (cropRect.y + cropRect.height > options.imageSize.height) {
      const centerY = cropRect.y + cropRect.height / 2;
      cropRect.height = 2 * (options.imageSize.height - centerY);
      cropRect.y = options.imageSize.height - cropRect.height;
    }

    if (cropRect.y + cropRect.height < 0) {
      const centerY = cropRect.y + cropRect.height / 2;
      cropRect.y = 2 * centerY;
      cropRect.height = -cropRect.y;
    }
  }

  override resizeS(cropRect: CropRect, options: CropTransformOptions): void {
    const nextOptions = {
      ...options,
      dy: -options.dy,
    };
    this.resizeN(cropRect, nextOptions);
  }

  override resizeW(cropRect: CropRect, options: CropTransformOptions): void {
    cropRect.x += options.dx;
    cropRect.width -= options.dx * 2;

    if (cropRect.x < 0) {
      cropRect.width += cropRect.x * 2;
      cropRect.x = 0;
    }

    if (cropRect.x >= options.imageSize.width) {
      cropRect.width += 2 * (cropRect.x - options.imageSize.width + 1);
      cropRect.x = options.imageSize.width - 1;
    }

    if (cropRect.x + cropRect.width > options.imageSize.width) {
      const centerX = cropRect.x + cropRect.width / 2;
      cropRect.width = 2 * (options.imageSize.width - centerX);
      cropRect.x = options.imageSize.width - cropRect.width;
    }

    if (cropRect.x + cropRect.width < 0) {
      const centerX = cropRect.x + cropRect.width / 2;
      cropRect.x = 2 * centerX;
      cropRect.width = -cropRect.x;
    }
  }

  override resizeE(cropRect: CropRect, options: CropTransformOptions): void {
    const nextOptions = {
      ...options,
      dx: -options.dx,
    };
    this.resizeW(cropRect, nextOptions);
  }
}

class BaseKeepAspectTransformStrategy {
  constructor(private readonly aspectRatio: number) {}

  protected resizeWidthToAspectRatio(
    cropRect: CropRect,
    horizontalAlignment: CropAnchorAlignment,
  ): void {
    const newWidth = cropRect.height * this.aspectRatio;

    switch (horizontalAlignment) {
      case CropAnchorAlignment.Center:
        cropRect.x += (cropRect.width - newWidth) / 2;
        break;
      case CropAnchorAlignment.Far:
        cropRect.x += cropRect.width - newWidth;
        break;
    }

    cropRect.width = newWidth;
  }

  protected resizeHeightToAspectRatio(
    cropRect: CropRect,
    verticalAlignment: CropAnchorAlignment,
  ): void {
    const newHeight = cropRect.width / this.aspectRatio;

    switch (verticalAlignment) {
      case CropAnchorAlignment.Center:
        cropRect.y += (cropRect.height - newHeight) / 2;
        break;
      case CropAnchorAlignment.Far:
        cropRect.y += cropRect.height - newHeight;
        break;
    }

    cropRect.height = newHeight;
  }

  protected correctXdim(
    cropRect: CropRect,
    imageSize: CropSize,
    horizontalAlignment: CropAnchorAlignment,
    verticalAlignment: CropAnchorAlignment,
  ): void {
    const normalizedRect = normalizeCropRect(cropRect);

    if (
      normalizedRect &&
      (normalizedRect.x < 0 || normalizedRect.x + normalizedRect.width > imageSize.width)
    ) {
      const halfWidth = normalizedRect.width / 2;
      const centerX = normalizedRect.x + halfWidth;
      const scaleX = Math.max(
        halfWidth / centerX,
        halfWidth / (imageSize.width - centerX),
      );
      const anchorPoint = getCropRectAnchorPoint(
        normalizedRect,
        horizontalAlignment,
        verticalAlignment,
      );
      Object.assign(cropRect, scaleCropRect(cropRect, anchorPoint, 1 / scaleX));
    }
  }

  protected correctYdim(
    cropRect: CropRect,
    imageSize: CropSize,
    horizontalAlignment: CropAnchorAlignment,
    verticalAlignment: CropAnchorAlignment,
  ): void {
    const normalizedRect = normalizeCropRect(cropRect);

    if (
      normalizedRect &&
      (normalizedRect.y < 0 || normalizedRect.y + normalizedRect.height > imageSize.height)
    ) {
      const halfHeight = normalizedRect.height / 2;
      const centerY = normalizedRect.y + halfHeight;
      const scaleY = Math.max(
        halfHeight / centerY,
        halfHeight / (imageSize.height - centerY),
      );
      const anchorPoint = getCropRectAnchorPoint(
        normalizedRect,
        horizontalAlignment,
        verticalAlignment,
      );
      Object.assign(cropRect, scaleCropRect(cropRect, anchorPoint, 1 / scaleY));
    }
  }

  protected correctXYdim(
    cropRect: CropRect,
    imageSize: CropSize,
    horizontalAlignment: CropAnchorAlignment,
    verticalAlignment: CropAnchorAlignment,
  ): void {
    const normalizedRect = normalizeCropRect(cropRect);

    if (
      normalizedRect &&
      (
        normalizedRect.x < 0 ||
        normalizedRect.x + normalizedRect.width > imageSize.width ||
        normalizedRect.y < 0 ||
        normalizedRect.y + normalizedRect.height > imageSize.height
      )
    ) {
      const halfWidth = normalizedRect.width / 2;
      const halfHeight = normalizedRect.height / 2;
      const centerX = normalizedRect.x + halfWidth;
      const centerY = normalizedRect.y + halfHeight;
      const scale = Math.max(
        halfWidth / centerX,
        halfWidth / (imageSize.width - centerX),
        halfHeight / centerY,
        halfHeight / (imageSize.height - centerY),
      );
      const anchorPoint = getCropRectAnchorPoint(
        normalizedRect,
        horizontalAlignment,
        verticalAlignment,
      );
      Object.assign(cropRect, scaleCropRect(cropRect, anchorPoint, 1 / scale));
    }
  }
}

class EdgeKeepAspectTransformStrategy
  extends BaseKeepAspectTransformStrategy
  implements TransformStrategy
{
  private readonly transformer = new EdgeFreeAspectTransformStrategy();

  resizeN(cropRect: CropRect, options: CropTransformOptions): void {
    this.transformer.resizeN(cropRect, options);
    this.resizeWidthToAspectRatio(cropRect, CropAnchorAlignment.Center);
    this.correctXdim(
      cropRect,
      options.imageSize,
      CropAnchorAlignment.Center,
      CropAnchorAlignment.Far,
    );
  }

  resizeS(cropRect: CropRect, options: CropTransformOptions): void {
    this.transformer.resizeS(cropRect, options);
    this.resizeWidthToAspectRatio(cropRect, CropAnchorAlignment.Center);
    this.correctXdim(
      cropRect,
      options.imageSize,
      CropAnchorAlignment.Center,
      CropAnchorAlignment.Near,
    );
  }

  resizeW(cropRect: CropRect, options: CropTransformOptions): void {
    this.transformer.resizeW(cropRect, options);
    this.resizeHeightToAspectRatio(cropRect, CropAnchorAlignment.Center);
    this.correctYdim(
      cropRect,
      options.imageSize,
      CropAnchorAlignment.Far,
      CropAnchorAlignment.Center,
    );
  }

  resizeE(cropRect: CropRect, options: CropTransformOptions): void {
    this.transformer.resizeE(cropRect, options);
    this.resizeHeightToAspectRatio(cropRect, CropAnchorAlignment.Center);
    this.correctYdim(
      cropRect,
      options.imageSize,
      CropAnchorAlignment.Near,
      CropAnchorAlignment.Center,
    );
  }

  resizeNW(cropRect: CropRect, options: CropTransformOptions): void {
    this.transformer.resizeN(cropRect, options);
    this.resizeWidthToAspectRatio(cropRect, CropAnchorAlignment.Far);
    this.correctXYdim(
      cropRect,
      options.imageSize,
      cropRect.width < 0 ? CropAnchorAlignment.Near : CropAnchorAlignment.Far,
      cropRect.width < 0 ? CropAnchorAlignment.Near : CropAnchorAlignment.Far,
    );
  }

  resizeNE(cropRect: CropRect, options: CropTransformOptions): void {
    this.transformer.resizeN(cropRect, options);
    this.resizeWidthToAspectRatio(cropRect, CropAnchorAlignment.Near);
    this.correctXYdim(
      cropRect,
      options.imageSize,
      cropRect.width < 0 ? CropAnchorAlignment.Far : CropAnchorAlignment.Near,
      cropRect.width < 0 ? CropAnchorAlignment.Near : CropAnchorAlignment.Far,
    );
  }

  resizeSW(cropRect: CropRect, options: CropTransformOptions): void {
    this.transformer.resizeS(cropRect, options);
    this.resizeWidthToAspectRatio(cropRect, CropAnchorAlignment.Far);
    this.correctXYdim(
      cropRect,
      options.imageSize,
      cropRect.width < 0 ? CropAnchorAlignment.Near : CropAnchorAlignment.Far,
      cropRect.width < 0 ? CropAnchorAlignment.Far : CropAnchorAlignment.Near,
    );
  }

  resizeSE(cropRect: CropRect, options: CropTransformOptions): void {
    this.transformer.resizeS(cropRect, options);
    this.resizeWidthToAspectRatio(cropRect, CropAnchorAlignment.Near);
    this.correctXYdim(
      cropRect,
      options.imageSize,
      cropRect.width < 0 ? CropAnchorAlignment.Far : CropAnchorAlignment.Near,
      cropRect.width < 0 ? CropAnchorAlignment.Far : CropAnchorAlignment.Near,
    );
  }
}

class CenterKeepAspectTransformStrategy
  extends BaseKeepAspectTransformStrategy
  implements TransformStrategy
{
  private readonly transformer = new CenterFreeAspectTransformStrategy();

  resizeN(cropRect: CropRect, options: CropTransformOptions): void {
    this.transformer.resizeN(cropRect, options);
    this.resizeWidthToAspectRatio(cropRect, CropAnchorAlignment.Center);
    this.correctXdim(
      cropRect,
      options.imageSize,
      CropAnchorAlignment.Center,
      CropAnchorAlignment.Center,
    );
  }

  resizeS(cropRect: CropRect, options: CropTransformOptions): void {
    this.transformer.resizeS(cropRect, options);
    this.resizeWidthToAspectRatio(cropRect, CropAnchorAlignment.Center);
    this.correctXdim(
      cropRect,
      options.imageSize,
      CropAnchorAlignment.Center,
      CropAnchorAlignment.Center,
    );
  }

  resizeW(cropRect: CropRect, options: CropTransformOptions): void {
    this.transformer.resizeW(cropRect, options);
    this.resizeHeightToAspectRatio(cropRect, CropAnchorAlignment.Center);
    this.correctYdim(
      cropRect,
      options.imageSize,
      CropAnchorAlignment.Center,
      CropAnchorAlignment.Center,
    );
  }

  resizeE(cropRect: CropRect, options: CropTransformOptions): void {
    this.transformer.resizeE(cropRect, options);
    this.resizeHeightToAspectRatio(cropRect, CropAnchorAlignment.Center);
    this.correctYdim(
      cropRect,
      options.imageSize,
      CropAnchorAlignment.Center,
      CropAnchorAlignment.Center,
    );
  }

  resizeNW(cropRect: CropRect, options: CropTransformOptions): void {
    this.resizeN(cropRect, options);
  }

  resizeNE(cropRect: CropRect, options: CropTransformOptions): void {
    this.resizeN(cropRect, options);
  }

  resizeSW(cropRect: CropRect, options: CropTransformOptions): void {
    this.resizeS(cropRect, options);
  }

  resizeSE(cropRect: CropRect, options: CropTransformOptions): void {
    this.resizeS(cropRect, options);
  }
}

const createTransformStrategy = (
  aspectRatio: number,
  centerAsBase: boolean,
): TransformStrategy => {
  if (!aspectRatio) {
    return centerAsBase
      ? new CenterFreeAspectTransformStrategy()
      : new EdgeFreeAspectTransformStrategy();
  }

  return centerAsBase
    ? new CenterKeepAspectTransformStrategy(aspectRatio)
    : new EdgeKeepAspectTransformStrategy(aspectRatio);
};

export const getCropAspectRatio = (options: CropOptions | null | undefined): number => {
  if (!options || options.units !== CropUnit.AspectRatio || !options.width || !options.height) {
    return 0;
  }

  return options.width / options.height;
};

export class CropCalculator {
  getInitialCropRect(cropOptions: CropOptions, imageSize: CropSize): CropRect {
    let rectWidth: number;
    let rectHeight: number;

    switch (cropOptions.units) {
      case CropUnit.Pixels:
        rectWidth = cropOptions.width;
        rectHeight = cropOptions.height;
        break;
      case CropUnit.Percentage:
        rectWidth = (cropOptions.width * imageSize.width) / 100;
        rectHeight = (cropOptions.height * imageSize.height) / 100;
        break;
      case CropUnit.AspectRatio: {
        const scale = Math.min(
          imageSize.width / cropOptions.width,
          imageSize.height / cropOptions.height,
        );
        rectWidth = Math.min(
          imageSize.width,
          Math.round(cropOptions.width * scale * INITIAL_ASPECT_RATIO_CROP_SCALE),
        );
        rectHeight = Math.min(
          imageSize.height,
          Math.round(cropOptions.height * scale * INITIAL_ASPECT_RATIO_CROP_SCALE),
        );
        break;
      }
      default:
        throw new Error(`Unsupported crop unit: ${String(cropOptions.units)}`);
    }

    return {
      x: (imageSize.width - rectWidth) / 2,
      y: (imageSize.height - rectHeight) / 2,
      width: Math.max(1, rectWidth),
      height: Math.max(1, rectHeight),
    };
  }

  transformCropRect(cropRect: CropRect, options: CropTransformOptions): void {
    if (options.operation === CropOperation.None) {
      return;
    }

    if (options.operation === CropOperation.Move) {
      cropRect.x = Math.max(
        0,
        Math.min(cropRect.x + options.dx, options.imageSize.width - 1 - cropRect.width),
      );
      cropRect.y = Math.max(
        0,
        Math.min(cropRect.y + options.dy, options.imageSize.height - 1 - cropRect.height),
      );
      return;
    }

    const transformer = createTransformStrategy(options.aspectRatio, options.centerAsBase);

    switch (options.operation) {
      case CropOperation.ResizeN:
        transformer.resizeN(cropRect, options);
        break;
      case CropOperation.ResizeS:
        transformer.resizeS(cropRect, options);
        break;
      case CropOperation.ResizeE:
        transformer.resizeE(cropRect, options);
        break;
      case CropOperation.ResizeW:
        transformer.resizeW(cropRect, options);
        break;
      case CropOperation.ResizeNE:
        transformer.resizeNE(cropRect, options);
        break;
      case CropOperation.ResizeNW:
        transformer.resizeNW(cropRect, options);
        break;
      case CropOperation.ResizeSE:
        transformer.resizeSE(cropRect, options);
        break;
      case CropOperation.ResizeSW:
        transformer.resizeSW(cropRect, options);
        break;
    }
  }

  normalizeAndClampCropRect(cropRect: CropRect | null | undefined, imageSize: CropSize): CropRect | null {
    return clampCropRectToBounds(normalizeCropRect(cropRect), imageSize);
  }
}
